import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../contexts/TenantContext';
import { useDashboardData } from '../contexts/DashboardDataContext';
import {
  ArrowLeft,
  MessageSquare,
  Users,
  Clock,
  Send,
  Calendar,
  AlertCircle,
  CheckCircle,
  Upload,
  Filter,
  Info,
  Zap,
  Save,
  Wallet,
  Lock,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Link2,
  User,
  ChevronDown,
  Tag,
  Loader2,
  ExternalLink,
  Mail,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../components/ui/alert-dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

import { DEFAULT_SMS_COST_CENTS } from '../lib/sms-pricing';

// Opt-out suffix auto-appended to every SMS (Spam Act 2003)
const SMS_OPT_OUT_SUFFIX = "\nOpt out: Reply STOP";

// SMS segment calculation (includes opt-out suffix)
const calculateSegments = (message) => {
  if (!message) return { chars: 0, segments: 0, charsPerSegment: 160, totalChars: 0 };
  const fullMessage = message.toLowerCase().includes('opt out: reply stop') ? message : message + SMS_OPT_OUT_SUFFIX;
  const len = fullMessage.length;
  // Check for unicode — strip merge tags first since they get replaced with plain text before sending
  const messageWithoutTags = message.replace(/\{first_name\}/g, '');
  const gsm7Chars = new Set('@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà');
  const isUnicode = ![...messageWithoutTags].every(c => gsm7Chars.has(c));
  
  const singleSegmentLimit = isUnicode ? 70 : 160;
  const multiSegmentLimit = isUnicode ? 67 : 153;
  
  let segments;
  if (len === 0) {
    segments = 0;
  } else if (len <= singleSegmentLimit) {
    segments = 1;
  } else {
    // For multi-part messages, use the reduced character limit per segment
    segments = Math.ceil(len / multiSegmentLimit);
  }
  
  return { chars: message.length, totalChars: len, segments, charsPerSegment: singleSegmentLimit, isUnicode };
};

// Check if a message contains personalisation tags
const messageHasTags = (msg) => /\{first_name\}/.test(msg);

export default function CreateCampaignPage() {
  const navigate = useNavigate();
  const { getToken } = useTenant();
  const { refreshWallet } = useDashboardData() || {};

  // Channel state
  const [channel, setChannel] = useState('sms'); // 'sms' | 'email'
  const [emailSubject, setEmailSubject] = useState('');
  const [emailHtml, setEmailHtml] = useState('');
  const [emailFromName, setEmailFromName] = useState('');
  const [senderIdentities, setSenderIdentities] = useState([]);
  const [selectedSenderId, setSelectedSenderId] = useState('');

  // AI email generation state
  const [emailPrompt, setEmailPrompt] = useState('');
  const [emailTone, setEmailTone] = useState('friendly');
  const [isGeneratingEmail, setIsGeneratingEmail] = useState(false);
  const [showHtmlEditor, setShowHtmlEditor] = useState(false);

  // Email image upload state
  const [emailImageUrl, setEmailImageUrl] = useState('');
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const emailImageInputRef = useRef(null);

  // Email CTA state
  const [ctaText, setCtaText] = useState('');
  const [ctaLink, setCtaLink] = useState('');

  // Form state
  const [campaignName, setCampaignName] = useState('');
  const [messageBody, setMessageBody] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [targetType, setTargetType] = useState('all'); // 'all', 'segment', 'manual'
  const [selectedSegmentId, setSelectedSegmentId] = useState('');
  const [manualNumbers, setManualNumbers] = useState('');
  const [sendType, setSendType] = useState('now'); // 'now', 'scheduled'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  // Data state
  const [segments, setSegments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [walletStatus, setWalletStatus] = useState(null);
  const [customerCount, setCustomerCount] = useState(0);
  const [settings, setSettings] = useState(null);

  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Send mode gate — must be explicitly unlocked before showing recipients/send options
  const [sendModeUnlocked, setSendModeUnlocked] = useState(false);

  // Tag info dialog — shown once per session on first tag insert
  const [showTagInfoDialog, setShowTagInfoDialog] = useState(false);
  const tagInfoShownRef = useRef(false);

  // Backend cost estimate (when tags are present)
  const [costEstimate, setCostEstimate] = useState(null);
  const costEstimateTimerRef = useRef(null);

  // Link popover state
  const [linkUrl, setLinkUrl] = useState('');
  const [linkShortening, setLinkShortening] = useState(false);
  const [linkError, setLinkError] = useState('');
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);

  // Smart promo detection
  const [detectedPromo, setDetectedPromo] = useState(null);

  // Advanced section (promo code) collapsed state
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Calculate message segments (URL now lives inside messageBody)
  const messageInfo = calculateSegments(messageBody);

  const campaignMessageRef = useRef(null);

  const insertTag = (tag) => {
    const textarea = campaignMessageRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = messageBody.slice(0, start);
    const after = messageBody.slice(end);
    const newMessage = before + tag + after;

    // Show info dialog on first tag insert per session
    if (!tagInfoShownRef.current && !messageHasTags(messageBody)) {
      setShowTagInfoDialog(true);
      tagInfoShownRef.current = true;
    }

    setMessageBody(newMessage);
    requestAnimationFrame(() => {
      textarea.focus();
      textarea.selectionStart = textarea.selectionEnd = start + tag.length;
    });
  };

  // Smart promo code detection — watch messageBody for promo-like patterns
  useEffect(() => {
    if (!messageBody) {
      setDetectedPromo(null);
      return;
    }
    const match = messageBody.match(/(?:code|promo|coupon|discount|voucher)[:\s]+([A-Z0-9]{3,20})/i);
    if (match) {
      setDetectedPromo(match[1].toUpperCase());
    } else {
      setDetectedPromo(null);
    }
  }, [messageBody]);

  // Shorten a URL and insert at cursor
  const handleShortenAndInsert = async () => {
    const url = linkUrl.trim();
    if (!url) return;
    setLinkShortening(true);
    setLinkError('');
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/links/shorten`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to shorten URL');
      }
      const data = await res.json();
      insertTag(data.short_url);
      setLinkUrl('');
      setLinkPopoverOpen(false);
    } catch (err) {
      setLinkError(err.message);
    } finally {
      setLinkShortening(false);
    }
  };

  // Upload image for email via presigned R2 flow
  const handleEmailImageUpload = async (file) => {
    if (!file) return;
    setIsUploadingImage(true);
    try {
      const token = await getToken();
      const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

      // 1. Get presigned upload URL
      const presignRes = await fetch(`${BACKEND_URL}/api/files/presigned-upload`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          file_name: file.name,
          mime_type: file.type,
          file_size_bytes: file.size,
          purpose: 'email_image',
        }),
      });
      if (!presignRes.ok) throw new Error('Failed to get upload URL');
      const { upload_url, file_id } = await presignRes.json();

      // 2. Upload directly to R2
      await fetch(upload_url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      // 3. Confirm upload
      const confirmRes = await fetch(`${BACKEND_URL}/api/files/confirm-upload`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ file_id }),
      });
      if (!confirmRes.ok) throw new Error('Failed to confirm upload');

      // 4. Make file public
      const publicRes = await fetch(`${BACKEND_URL}/api/files/${file_id}/make-public`, {
        method: 'PATCH',
        headers,
      });
      if (!publicRes.ok) throw new Error('Failed to make file public');

      // 5. Set the public URL
      setEmailImageUrl(`${BACKEND_URL}/api/files/public/${file_id}`);
    } catch (err) {
      setErrors((prev) => ({ ...prev, message: `Image upload failed: ${err.message}` }));
    } finally {
      setIsUploadingImage(false);
    }
  };

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      try {
        const token = await getToken();
        const headers = { 'Authorization': `Bearer ${token}` };
        
        const [segmentsRes, templatesRes, walletRes, customersRes, settingsRes, sendersRes] = await Promise.all([
          fetch(`${BACKEND_URL}/api/segments`, { headers }),
          fetch(`${BACKEND_URL}/api/templates`, { headers }),
          fetch(`${BACKEND_URL}/api/stripe/subscription-status`, { headers }),
          fetch(`${BACKEND_URL}/api/customers?page_size=1&opt_in_only=true`, { headers }),
          fetch(`${BACKEND_URL}/api/settings`, { headers }),
          fetch(`${BACKEND_URL}/api/email-sender/identities`, { headers }),
        ]);

        if (segmentsRes.ok) {
          const data = await segmentsRes.json();
          setSegments(data.segments || []);
        }
        if (templatesRes.ok) {
          const data = await templatesRes.json();
          setTemplates(data.templates || []);
        }
        if (walletRes.ok) {
          setWalletStatus(await walletRes.json());
        }
        if (customersRes.ok) {
          const data = await customersRes.json();
          setCustomerCount(data.total || 0);
        }
        if (settingsRes.ok) {
          setSettings(await settingsRes.json());
        }
        if (sendersRes.ok) {
          const data = await sendersRes.json();
          const identities = data.identities || [];
          setSenderIdentities(identities);
          const verified = identities.find(i => i.verified);
          if (verified) setSelectedSenderId(verified.id);
        }
      } catch (err) {
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getToken]);

  // Debounced backend cost estimate when message has personalisation tags
  useEffect(() => {
    // Clear previous timer
    if (costEstimateTimerRef.current) {
      clearTimeout(costEstimateTimerRef.current);
    }

    // Only fetch if message has tags and there are recipients
    if (!messageHasTags(messageBody) || !messageBody.trim()) {
      setCostEstimate(null);
      return;
    }

    costEstimateTimerRef.current = setTimeout(async () => {
      try {
        const token = await getToken();

        // Build target_filter matching handleSubmit logic
        let targetFilter = {};
        if (targetType === 'all') {
          targetFilter = { type: 'all' };
        } else if (targetType === 'segment' && selectedSegmentId) {
          targetFilter = { type: 'segment', segment_id: selectedSegmentId };
        } else if (targetType === 'manual') {
          const entries = manualNumbers.split(/[\n,]/).map(n => n.trim()).filter(Boolean);
          targetFilter = channel === 'email'
            ? { type: 'manual', email_addresses: entries }
            : { type: 'manual', phone_numbers: entries };
        }

        const res = await fetch(`${BACKEND_URL}/api/campaigns/estimate-cost`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            message_template: messageBody,
            target_filter: targetFilter,
          }),
        });

        if (res.ok) {
          setCostEstimate(await res.json());
        }
      } catch (err) {
        console.error('Cost estimate failed:', err);
      }
    }, 500);

    return () => {
      if (costEstimateTimerRef.current) {
        clearTimeout(costEstimateTimerRef.current);
      }
    };
  }, [messageBody, targetType, selectedSegmentId, manualNumbers, getToken]);

  // Calculate recipients based on target type
  const getRecipientCount = useCallback(() => {
    if (targetType === 'all') {
      return customerCount;
    } else if (targetType === 'segment' && selectedSegmentId) {
      const segment = segments.find(s => s.id === selectedSegmentId);
      return segment?.customer_count ?? 0;
    } else if (targetType === 'manual') {
      const numbers = manualNumbers.split(/[\n,]/).filter(n => n.trim());
      return numbers.length;
    }
    return 0;
  }, [targetType, customerCount, selectedSegmentId, segments, manualNumbers]);
  
  const recipientCount = getRecipientCount();
  const totalSmsRequired = recipientCount * messageInfo.segments;
  // Use actual SMS cost from wallet status (pack-based), fallback to default
  const smsCostCents = walletStatus?.sms_cost_cents || DEFAULT_SMS_COST_CENTS;
  const estimatedCostCents = totalSmsRequired * smsCostCents;
  
  // SMS pack balance (the real balance in the pack-based system)
  const smsBalance = walletStatus?.sms_capacity || 0;
  const smsAfterSend = smsBalance - totalSmsRequired;
  const canAfford = smsBalance >= totalSmsRequired;
  
  // Quiet hours check
  const quietHoursStart = settings?.quiet_hours_start || '21:00';
  const quietHoursEnd = settings?.quiet_hours_end || '09:00';
  const businessTimezone = settings?.timezone || 'Australia/Sydney';
  
  // Check if current time is in quiet hours (for "Send Now")
  const isCurrentlyInQuietHours = useCallback(() => {
    try {
      // Get current time in business timezone
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-AU', {
        timeZone: businessTimezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const currentTime = formatter.format(now);
      const currentHour = parseInt(currentTime.split(':')[0], 10);
      const currentMinute = parseInt(currentTime.split(':')[1], 10);
      
      const startHour = parseInt(quietHoursStart.split(':')[0], 10);
      const startMinute = parseInt((quietHoursStart.split(':')[1] || '00'), 10);
      const endHour = parseInt(quietHoursEnd.split(':')[0], 10);
      const endMinute = parseInt((quietHoursEnd.split(':')[1] || '00'), 10);
      
      const currentMinutes = currentHour * 60 + currentMinute;
      const startMinutes = startHour * 60 + startMinute;
      const endMinutes = endHour * 60 + endMinute;
      
      if (startMinutes > endMinutes) {
        // Quiet hours span midnight (e.g., 21:00 - 09:00)
        return currentMinutes >= startMinutes || currentMinutes < endMinutes;
      } else {
        return currentMinutes >= startMinutes && currentMinutes < endMinutes;
      }
    } catch {
      return false;
    }
  }, [quietHoursStart, quietHoursEnd, businessTimezone]);
  
  const isInQuietHours = useCallback((date, time) => {
    if (!date || !time) return false;
    const hour = parseInt(time.split(':')[0], 10);
    const minute = parseInt((time.split(':')[1] || '00'), 10);
    const startHour = parseInt(quietHoursStart.split(':')[0], 10);
    const startMinute = parseInt((quietHoursStart.split(':')[1] || '00'), 10);
    const endHour = parseInt(quietHoursEnd.split(':')[0], 10);
    const endMinute = parseInt((quietHoursEnd.split(':')[1] || '00'), 10);
    
    const timeMinutes = hour * 60 + minute;
    const startMinutes = startHour * 60 + startMinute;
    const endMinutes = endHour * 60 + endMinute;
    
    if (startMinutes > endMinutes) {
      // Quiet hours span midnight (e.g., 21:00 - 09:00)
      return timeMinutes >= startMinutes || timeMinutes < endMinutes;
    } else {
      return timeMinutes >= startMinutes && timeMinutes < endMinutes;
    }
  }, [quietHoursStart, quietHoursEnd]);
  
  // Track current quiet hours status for "Send Now"
  const [currentlyInQuietHours, setCurrentlyInQuietHours] = useState(false);
  
  useEffect(() => {
    // Check immediately
    setCurrentlyInQuietHours(isCurrentlyInQuietHours());
    
    // Re-check every minute
    const interval = setInterval(() => {
      setCurrentlyInQuietHours(isCurrentlyInQuietHours());
    }, 60000);
    
    return () => clearInterval(interval);
  }, [isCurrentlyInQuietHours]);
  
  const scheduledInQuietHours = sendType === 'scheduled' && isInQuietHours(scheduledDate, scheduledTime);
  const sendNowInQuietHours = sendType === 'now' && currentlyInQuietHours;

  // Email campaigns: no SMS balance check, just need recipients + verified sender
  const isEmailChannel = channel === 'email';
  const emailReady = isEmailChannel && emailSubject.trim() && emailHtml.trim() && selectedSenderId;
  const canSend = isEmailChannel
    ? (emailReady && recipientCount > 0 && !sendNowInQuietHours)
    : (canAfford && totalSmsRequired > 0 && !sendNowInQuietHours);
  
  
  // Validation
  const validate = () => {
    const newErrors = {};

    if (!campaignName.trim()) {
      newErrors.name = 'Campaign name is required';
    }

    if (isEmailChannel) {
      // Email validation
      if (!emailSubject.trim()) newErrors.emailSubject = 'Email subject is required';
      if (!emailHtml.trim()) newErrors.message = 'Email body is required';
      if (!selectedSenderId) newErrors.sender = 'Please verify a sender email in Settings first';
    } else {
      // SMS validation
      if (!messageBody.trim()) newErrors.message = 'Message is required';
      if (!canAfford) {
        newErrors.balance = `Not enough SMS credits. Need ${totalSmsRequired.toLocaleString()} SMS, have ${smsBalance.toLocaleString()} SMS`;
      }
    }

    if (promoCode && !/^[a-zA-Z0-9]+$/.test(promoCode)) {
      newErrors.promoCode = 'Promo code must be alphanumeric (letters and numbers only)';
    }
    if (promoCode && promoCode.length > 20) {
      newErrors.promoCode = 'Promo code too long (max 20 characters)';
    }
    if (targetType === 'segment' && !selectedSegmentId) {
      newErrors.target = 'Please select a segment';
    }
    if (targetType === 'manual' && !manualNumbers.trim()) {
      newErrors.target = isEmailChannel ? 'Please enter email addresses' : 'Please enter phone numbers';
    }
    if (recipientCount === 0) {
      newErrors.recipients = 'No valid recipients';
    }
    if (sendType === 'scheduled') {
      if (!scheduledDate) newErrors.schedule = 'Please select a date';
      if (!scheduledTime) newErrors.schedule = 'Please select a time';
      if (scheduledInQuietHours) {
        newErrors.schedule = `Cannot schedule during quiet hours (${quietHoursStart} - ${quietHoursEnd})`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Build human-readable target description for confirmation
  const getTargetDescription = () => {
    if (targetType === 'all') return 'ALL customers';
    if (targetType === 'segment') {
      const segment = segments.find(s => s.id === selectedSegmentId);
      return `segment "${segment?.name || 'Unknown'}"`;
    }
    if (targetType === 'manual') return isEmailChannel ? 'manually entered emails' : 'manually entered numbers';
    return 'unknown';
  };

  // Submit campaign
  // Send test SMS to a single number
  const [testPhone, setTestPhone] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleSendTest = async () => {
    if (!testPhone.trim() || !messageBody.trim()) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const token = await getToken();
      const res = await fetch(`${BACKEND_URL}/api/campaigns/test-sms`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: testPhone.trim(), message_template: messageBody }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setTestResult({ success: true, message: 'Test SMS sent!' });
      } else {
        setTestResult({ success: false, message: data.detail || data.error || 'Failed to send test' });
      }
    } catch (err) {
      setTestResult({ success: false, message: err.message });
    } finally {
      setTestSending(false);
    }
  };

  // Send confirmation modal state
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [sendConfirmData, setSendConfirmData] = useState(null);

  // OTP verification state
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState(null);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [pendingSendData, setPendingSendData] = useState(null);

  const handleSubmit = async (saveAsDraft = false) => {
    if (!saveAsDraft && !validate()) return;

    // Build target filter based on selection
    let targetFilter = {};
    if (targetType === 'all') {
      targetFilter = { type: 'all' };
    } else if (targetType === 'segment') {
      targetFilter = { type: 'segment', segment_id: selectedSegmentId };
    } else if (targetType === 'manual') {
      const entries = manualNumbers.split(/[\n,]/).map(n => n.trim()).filter(Boolean);
      targetFilter = isEmailChannel
        ? { type: 'manual', email_addresses: entries }
        : { type: 'manual', phone_numbers: entries };
    }

    if (!saveAsDraft) {
      // SAFETY: Fetch real recipient count from backend before confirming
      let backendRecipientCount = recipientCount;
      try {
        const token = await getToken();
        const estimateRes = await fetch(`${BACKEND_URL}/api/campaigns/estimate-cost`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ message_template: messageBody, target_filter: targetFilter }),
        });
        if (estimateRes.ok) {
          const est = await estimateRes.json();
          backendRecipientCount = est.total_recipients;
        }
      } catch {
        // If estimate fails, use frontend count
      }

      const effectiveSms = costEstimate ? costEstimate.total_segments : backendRecipientCount * messageInfo.segments;
      const effectiveCostCents = effectiveSms * smsCostCents;

      // Show confirmation modal instead of window.confirm
      setSendConfirmData({
        targetFilter,
        backendRecipientCount,
        effectiveSms,
        effectiveCostCents,
      });
      setShowSendConfirm(true);
      return;
    }

    // Draft path — no confirmation needed
    executeSend(true, targetFilter, recipientCount);
  };

  const executeSend = async (saveAsDraft, targetFilter, backendRecipientCount) => {
    setShowSendConfirm(false);
    setSubmitting(true);
    try {
      const token = await getToken();

      // Build scheduled time if applicable
      let scheduledAt = null;
      if (sendType === 'scheduled' && scheduledDate && scheduledTime) {
        scheduledAt = `${scheduledDate}T${scheduledTime}:00`;
      }

      const selectedSender = senderIdentities.find(i => i.id === selectedSenderId);
      const payload = {
        name: campaignName,
        channel,
        message_template: isEmailChannel ? (emailHtml || 'Email campaign') : messageBody,
        promo_code: promoCode.trim() || null,
        target_filter: targetFilter,
        scheduled_at: scheduledAt,
        status: saveAsDraft ? 'draft' : (sendType === 'scheduled' ? 'scheduled' : 'draft'),
        // Email-specific fields
        ...(isEmailChannel && {
          email_subject: emailSubject,
          email_html: emailHtml,
          email_from_name: emailFromName || undefined,
          email_reply_to: selectedSender?.identity_value || undefined,
        }),
      };

      // Create campaign
      const createRes = await fetch(`${BACKEND_URL}/api/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      // Clone before reading so we always have a fallback
      const createClone = createRes.clone();
      if (!createRes.ok) {
        let err;
        try { err = await createClone.json(); } catch { throw new Error('Failed to create campaign'); }
        throw new Error(err.detail || 'Failed to create campaign');
      }

      const campaign = await createRes.json();

      // If send now, trigger send with confirmed recipient count
      if (!saveAsDraft && sendType === 'now') {
        const sendRes = await fetch(`${BACKEND_URL}/api/campaigns/${campaign.id}/send`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ confirmed_recipient_count: backendRecipientCount }),
        });

        // Clone before reading to avoid "body stream already read" errors
        const sendClone = sendRes.clone();
        let sendData;
        try {
          sendData = await sendRes.json();
        } catch {
          try { sendData = await sendClone.json(); } catch { throw new Error('Failed to send campaign'); }
        }

        if (!sendRes.ok) {
          if (sendData.detail?.error_code === 'INSUFFICIENT_SMS_BALANCE') {
            setErrors({ balance: sendData.detail.message });
            setSubmitting(false);
            return;
          }
          if (sendData.detail?.error_code === 'RECIPIENT_COUNT_MISMATCH') {
            setErrors({ submit: sendData.detail.message });
            setSubmitting(false);
            return;
          }

          // Handle quiet hours error from server
          const errorMessage = typeof sendData.detail === 'string'
            ? sendData.detail
            : sendData.detail?.message || 'Failed to send campaign';
          throw new Error(errorMessage);
        }
      }
      
      // Refresh SMS balance in Topbar
      if (!saveAsDraft && refreshWallet) refreshWallet();

      // Navigate back to campaigns list
      navigate('/dashboard/campaigns', {
        state: {
          message: saveAsDraft
            ? 'Campaign saved as draft'
            : sendType === 'now'
              ? 'Campaign sent successfully!'
              : 'Campaign scheduled successfully!'
        }
      });
      
    } catch (err) {
      setErrors({ submit: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  // Send/schedule with OTP verification (called from OTP dialog)
  const executeSendWithOtp = async (targetFilter, backendRecipientCount, campaignId, code) => {
    setShowOtpDialog(false);
    setSubmitting(true);
    try {
      const token = await getToken();

      if (sendType === 'scheduled') {
        // For scheduled campaigns: update the existing draft campaign to scheduled with OTP
        let scheduledAt = null;
        if (scheduledDate && scheduledTime) {
          scheduledAt = `${scheduledDate}T${scheduledTime}:00`;
        }
        const selectedSender = senderIdentities.find(i => i.id === selectedSenderId);
        const payload = {
          name: campaignName,
          channel,
          message_template: isEmailChannel ? (emailHtml || 'Email campaign') : messageBody,
          promo_code: promoCode.trim() || null,
          target_filter: targetFilter,
          scheduled_at: scheduledAt,
          otp_code: code,
          ...(isEmailChannel && {
            email_subject: emailSubject,
            email_html: emailHtml,
            email_from_name: emailFromName || undefined,
            email_reply_to: selectedSender?.identity_value || undefined,
          }),
        };

        // Delete the draft campaign first, then create with scheduled status + OTP
        await fetch(`${BACKEND_URL}/api/campaigns/${campaignId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` },
        });

        const createRes = await fetch(`${BACKEND_URL}/api/campaigns`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const createClone = createRes.clone();
        if (!createRes.ok) {
          let err;
          try { err = await createClone.json(); } catch { throw new Error('Failed to schedule campaign'); }
          if (err.detail?.error_code === 'INVALID_OTP') {
            const otpErr = new Error('Invalid or expired verification code');
            otpErr.otpInvalid = true;
            throw otpErr;
          }
          throw new Error(err.detail?.message || err.detail || 'Failed to schedule campaign');
        }
      } else {
        // Send now: call /send with OTP
        const sendRes = await fetch(`${BACKEND_URL}/api/campaigns/${campaignId}/send`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ confirmed_recipient_count: backendRecipientCount, otp_code: code }),
        });

        const sendClone = sendRes.clone();
        let sendData;
        try {
          sendData = await sendRes.json();
        } catch {
          try { sendData = await sendClone.json(); } catch { throw new Error('Failed to send campaign'); }
        }

        if (!sendRes.ok) {
          if (sendData.detail?.error_code === 'INVALID_OTP') {
            const otpErr = new Error('Invalid or expired verification code');
            otpErr.otpInvalid = true;
            throw otpErr;
          }
          if (sendData.detail?.error_code === 'INSUFFICIENT_SMS_BALANCE') {
            setErrors({ balance: sendData.detail.message });
            setSubmitting(false);
            return;
          }
          if (sendData.detail?.error_code === 'RECIPIENT_COUNT_MISMATCH') {
            setErrors({ submit: sendData.detail.message });
            setSubmitting(false);
            return;
          }
          const errorMessage = typeof sendData.detail === 'string'
            ? sendData.detail
            : sendData.detail?.message || 'Failed to send campaign';
          throw new Error(errorMessage);
        }
      }

      // Refresh SMS balance in Topbar
      if (refreshWallet) refreshWallet();

      navigate('/dashboard/campaigns', {
        state: {
          message: sendType === 'now'
            ? 'Campaign sent successfully!'
            : 'Campaign scheduled successfully!'
        }
      });

    } catch (err) {
      if (err.otpInvalid) {
        // Re-open OTP dialog with error
        setShowOtpDialog(true);
        throw err;
      }
      setErrors({ submit: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  // Apply template
  const applyTemplate = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setMessageBody(template.message_body);
    }
  };
  
  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded-xl"></div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="create-campaign-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard/campaigns')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div className="min-w-0">
                <h1 className="text-xl font-bold text-gray-900 truncate">Create Campaign</h1>
                <p className="text-sm text-gray-500 truncate">Send {isEmailChannel ? 'emails' : 'SMS'} to your customers</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button
                variant="outline"
                onClick={() => handleSubmit(true)}
                disabled={submitting || !campaignName.trim()}
                className="w-full sm:w-auto justify-center"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Draft
              </Button>
              {sendModeUnlocked && (
                <Button
                  onClick={() => handleSubmit(false)}
                  disabled={submitting || !canSend || scheduledInQuietHours}
                  data-testid="submit-campaign-btn"
                  className="w-full sm:w-auto justify-center"
                >
                  {submitting ? 'Processing...' : sendType === 'now' ? 'Send Campaign' : 'Schedule Campaign'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">

            {/* Channel Toggle */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 mr-2">Channel</span>
                <button
                  type="button"
                  onClick={() => setChannel('sms')}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    channel === 'sms'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  SMS
                </button>
                <button
                  type="button"
                  onClick={() => setChannel('email')}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    channel === 'email'
                      ? 'bg-orange-500 text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Mail className="w-4 h-4" />
                  Email
                </button>
              </div>
            </div>

            {/* Section 1: Campaign Details */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isEmailChannel ? 'bg-blue-100' : 'bg-orange-100'}`}>
                    {isEmailChannel ? <Mail className="w-4 h-4 text-blue-600" /> : <MessageSquare className="w-4 h-4 text-orange-600" />}
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">Campaign Details</h2>
                    <p className="text-xs text-gray-500">{isEmailChannel ? 'Name your campaign and compose your email' : 'Name your campaign and write your message'}</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div>
                  <Label htmlFor="campaign-name">Campaign Name * <span className="font-normal text-gray-400">(internal use only — not shown to customers)</span></Label>
                  <Input
                    id="campaign-name"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="e.g., Weekend Special Offer"
                    className={errors.name ? 'border-red-500' : ''}
                    data-testid="campaign-name-input"
                  />
                  {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                </div>

                {/* Email Composer */}
                {isEmailChannel && (
                  <>
                    <div>
                      <Label htmlFor="email-from">From (Sender) *</Label>
                      {senderIdentities.filter(i => i.verified).length > 0 ? (
                        <select
                          id="email-from"
                          value={selectedSenderId}
                          onChange={(e) => setSelectedSenderId(e.target.value)}
                          className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
                        >
                          <option value="">Select verified sender...</option>
                          {senderIdentities.filter(i => i.verified).map(i => (
                            <option key={i.id} value={i.id}>{i.identity_value}</option>
                          ))}
                        </select>
                      ) : (
                        <div className="mt-1 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                          No verified sender emails. Go to{' '}
                          <button type="button" onClick={() => navigate('/dashboard/settings?tab=email')} className="underline font-medium">
                            Settings &gt; Email Senders
                          </button>{' '}
                          to verify one first.
                        </div>
                      )}
                      {errors.sender && <p className="text-xs text-red-500 mt-1">{errors.sender}</p>}
                    </div>
                    <div>
                      <Label htmlFor="email-from-name">Display Name</Label>
                      <Input
                        id="email-from-name"
                        value={emailFromName}
                        onChange={(e) => setEmailFromName(e.target.value)}
                        placeholder="e.g. Sarah's Salon"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email-subject">Subject Line *</Label>
                      <Input
                        id="email-subject"
                        value={emailSubject}
                        onChange={(e) => setEmailSubject(e.target.value)}
                        placeholder="e.g. This week's special offer!"
                        className={errors.emailSubject ? 'border-red-500 mt-1' : 'mt-1'}
                      />
                      {errors.emailSubject && <p className="text-xs text-red-500 mt-1">{errors.emailSubject}</p>}
                    </div>
                    {/* AI Prompt */}
                    <div>
                      <Label>Describe Your Email *</Label>
                      <p className="text-xs text-gray-500 mt-1 mb-2">
                        AI will generate a professional design. Use {'{first_name}'} and {'{business_name}'} for personalisation. An unsubscribe link is added automatically.
                      </p>
                      <Textarea
                        value={emailPrompt}
                        onChange={(e) => setEmailPrompt(e.target.value)}
                        placeholder="e.g. 20% off all haircuts this Friday, mention our new location in Bondi"
                        rows={3}
                      />
                    </div>

                    {/* Tone selector */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-500 font-medium">Tone:</span>
                      {['professional', 'friendly', 'casual', 'urgent'].map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setEmailTone(t)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                            emailTone === t
                              ? 'bg-orange-500 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {t.charAt(0).toUpperCase() + t.slice(1)}
                        </button>
                      ))}
                    </div>

                    {/* Image Upload */}
                    <div className="p-3 border border-dashed border-gray-300 rounded-lg bg-gray-50/50">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-gray-600">Hero Image (optional)</span>
                        {emailImageUrl && (
                          <button
                            type="button"
                            onClick={() => { setEmailImageUrl(''); if (emailImageInputRef.current) emailImageInputRef.current.value = ''; }}
                            className="text-xs text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        )}
                      </div>
                      {emailImageUrl ? (
                        <img src={emailImageUrl} alt="Email image" className="max-h-32 rounded-md object-cover" />
                      ) : (
                        <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                          <Upload className="w-4 h-4" />
                          {isUploadingImage ? (
                            <><Loader2 className="w-3 h-3 animate-spin" /> Uploading...</>
                          ) : (
                            'Upload logo or photo'
                          )}
                          <input
                            ref={emailImageInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={isUploadingImage}
                            onChange={(e) => handleEmailImageUpload(e.target.files?.[0])}
                          />
                        </label>
                      )}
                    </div>

                    {/* CTA Button Fields */}
                    <div>
                      <Label className="text-xs font-medium text-gray-600 mb-2 block">Call-to-Action Button (optional)</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="cta-text" className="text-xs text-gray-500">Button Text</Label>
                          <Input
                            id="cta-text"
                            value={ctaText}
                            onChange={(e) => setCtaText(e.target.value)}
                            placeholder="e.g. Book Now"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label htmlFor="cta-link" className="text-xs text-gray-500">Button Link</Label>
                          <Input
                            id="cta-link"
                            value={ctaLink}
                            onChange={(e) => setCtaLink(e.target.value)}
                            placeholder="https://your-website.com"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Generate / Regenerate buttons */}
                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        onClick={async () => {
                          if (!emailPrompt.trim()) return;
                          setIsGeneratingEmail(true);
                          try {
                            const token = await getToken();
                            const res = await fetch(`${BACKEND_URL}/api/campaigns/generate-email`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                Authorization: `Bearer ${token}`,
                              },
                              body: JSON.stringify({
                                prompt: emailPrompt,
                                tone: emailTone,
                                image_url: emailImageUrl || undefined,
                                cta_text: ctaText || undefined,
                                cta_link: ctaLink || undefined,
                              }),
                            });
                            if (!res.ok) {
                              const err = await res.json().catch(() => ({}));
                              throw new Error(err.detail || 'Generation failed');
                            }
                            const data = await res.json();
                            setEmailHtml(data.html);
                            if (data.subject_suggestion && !emailSubject) {
                              setEmailSubject(data.subject_suggestion);
                            }
                          } catch (err) {
                            setErrors(prev => ({ ...prev, message: err.message }));
                          } finally {
                            setIsGeneratingEmail(false);
                          }
                        }}
                        disabled={isGeneratingEmail || !emailPrompt.trim()}
                        className="bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        {isGeneratingEmail ? (
                          <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</>
                        ) : emailHtml.trim() ? (
                          <><Sparkles className="w-4 h-4 mr-2" /> Regenerate</>
                        ) : (
                          <><Sparkles className="w-4 h-4 mr-2" /> Generate Email</>
                        )}
                      </Button>

                      {emailHtml.trim() && (
                        <button
                          type="button"
                          onClick={() => setShowHtmlEditor(!showHtmlEditor)}
                          className="text-xs text-gray-500 hover:text-gray-700 underline"
                        >
                          {showHtmlEditor ? 'Hide HTML' : 'Edit HTML'}
                        </button>
                      )}
                    </div>

                    {/* Raw HTML editor (toggle) */}
                    {showHtmlEditor && (
                      <Textarea
                        value={emailHtml}
                        onChange={(e) => setEmailHtml(e.target.value)}
                        rows={12}
                        className="font-mono text-xs"
                      />
                    )}

                    {errors.message && <p className="text-xs text-red-500">{errors.message}</p>}
                  </>
                )}

                {/* SMS Composer */}
                {!isEmailChannel && templates.length > 0 && (
                  <div>
                    <Label>Use Template (optional)</Label>
                    <select
                      onChange={(e) => e.target.value && applyTemplate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                      defaultValue=""
                    >
                      <option value="">Select a template...</option>
                      {templates.map(t => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {!isEmailChannel && <div>
                  <Label htmlFor="message-body">Message *</Label>

                  {/* Composer Toolbar */}
                  <div className="flex items-center gap-1.5 my-1.5 flex-wrap">
                    <button
                      type="button"
                      onClick={() => insertTag('{first_name}')}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-orange-50 text-orange-700 border border-orange-200 rounded-md hover:bg-orange-100 transition-colors"
                    >
                      <User className="w-3 h-3" />
                      First Name
                    </button>

                    <Popover open={linkPopoverOpen} onOpenChange={setLinkPopoverOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors"
                        >
                          <Link2 className="w-3 h-3" />
                          Add Link
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80" align="start">
                        <div className="space-y-3">
                          <p className="text-sm font-medium text-gray-900">Shorten & insert link</p>
                          <Input
                            value={linkUrl}
                            onChange={(e) => { setLinkUrl(e.target.value); setLinkError(''); }}
                            placeholder="https://yoursite.com/offer"
                            className="text-sm"
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleShortenAndInsert(); } }}
                          />
                          {linkError && <p className="text-xs text-red-500">{linkError}</p>}
                          <Button
                            size="sm"
                            className="w-full"
                            onClick={handleShortenAndInsert}
                            disabled={linkShortening || !linkUrl.trim()}
                          >
                            {linkShortening ? (
                              <><Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> Shortening...</>
                            ) : (
                              <><Link2 className="w-3 h-3 mr-1.5" /> Shorten & Insert</>
                            )}
                          </Button>
                          <p className="text-xs text-gray-400">Creates a tracked short link and inserts it at cursor</p>
                        </div>
                      </PopoverContent>
                    </Popover>

                    <button
                      type="button"
                      onClick={() => navigate('/dashboard/templates?new=1')}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 rounded-md hover:bg-purple-100 transition-colors"
                    >
                      <Sparkles className="w-3 h-3" />
                      AI Writer
                    </button>
                  </div>

                  <div className={`rounded-md border ${errors.message ? 'border-red-500' : 'border-input'} focus-within:ring-1 focus-within:ring-ring overflow-hidden`}>
                    <Textarea
                      ref={campaignMessageRef}
                      id="message-body"
                      value={messageBody}
                      onChange={(e) => setMessageBody(e.target.value)}
                      placeholder="Hi! We have a special offer just for you..."
                      rows={5}
                      className="border-0 rounded-none shadow-none focus-visible:ring-0"
                      data-testid="campaign-message-input"
                    />
                    <div className="bg-gray-50 border-t border-gray-200 px-3 py-1.5 select-none">
                      <span className="text-sm text-gray-400 italic">Opt out: Reply STOP</span>
                    </div>
                  </div>
                  {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}

                  {/* SMS Info */}
                  <div className={`mt-3 p-3 rounded-lg ${messageInfo.isUnicode ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        {messageInfo.totalChars} chars total ({messageInfo.chars} + opt-out)
                      </span>
                      <span className={`font-medium ${messageInfo.segments > 1 ? 'text-orange-600' : 'text-gray-900'}`}>
                        {messageInfo.segments} SMS per recipient
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Opt-out text is locked and sent with every SMS</p>
                    {messageHasTags(messageBody) && (
                      <p className="text-xs text-orange-600 mt-1">
                        Tags get replaced with real names — SMS count may vary per recipient
                      </p>
                    )}
                    {messageInfo.segments > 1 && !messageInfo.isUnicode && (
                      <p className="text-xs text-gray-500 mt-1">
                        Messages over 160 chars use multiple SMS
                      </p>
                    )}
                    {messageInfo.isUnicode && (
                      <div className="mt-2 p-2 bg-red-100 rounded text-sm text-red-800">
                        <p className="font-semibold">Emojis or special characters detected — this costs more!</p>
                        <p className="text-xs mt-1">
                          Standard SMS: 160 chars = 1 SMS. With emojis: 70 chars = 1 SMS.
                          Your message currently uses <strong>{messageInfo.segments} SMS</strong> instead of {Math.ceil(messageInfo.totalChars / (messageInfo.totalChars <= 160 ? 160 : 153))} without special characters.
                          Remove emojis to reduce costs.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Smart promo code detection nudge */}
                  {detectedPromo && !promoCode && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3">
                      <Tag className="w-4 h-4 text-amber-600 flex-shrink-0" />
                      <p className="text-sm text-amber-800 flex-1">
                        We spotted "<strong>{detectedPromo}</strong>" in your message.
                      </p>
                      <button
                        type="button"
                        onClick={() => { setPromoCode(detectedPromo); setAdvancedOpen(true); }}
                        className="text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 px-2.5 py-1 rounded-md transition-colors whitespace-nowrap"
                      >
                        Track for ROI
                      </button>
                    </div>
                  )}
                  {detectedPromo && promoCode && detectedPromo !== promoCode && (
                    <div className="mt-3 p-2.5 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <p className="text-xs text-red-700">
                        Promo code mismatch: message says "<strong>{detectedPromo}</strong>" but tracking field says "<strong>{promoCode}</strong>"
                      </p>
                    </div>
                  )}

                  {/* Send Test SMS */}
                  {messageBody.trim() && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-800 mb-2">Send a test SMS first</p>
                      <div className="flex gap-2">
                        <Input
                          value={testPhone}
                          onChange={(e) => setTestPhone(e.target.value)}
                          placeholder="+61412345678"
                          className="flex-1 text-sm bg-white"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={handleSendTest}
                          disabled={testSending || !testPhone.trim()}
                          className="whitespace-nowrap"
                        >
                          {testSending ? 'Sending...' : 'Send Test'}
                        </Button>
                      </div>
                      {testResult && (
                        <p className={`text-xs mt-1.5 ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                          {testResult.message}
                        </p>
                      )}
                    </div>
                  )}
                </div>}

                {/* Advanced — Promo Code (collapsible) */}
                <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                    >
                      <ChevronDown className={`w-4 h-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                      Advanced
                      {promoCode && <span className="ml-1 px-1.5 py-0.5 bg-orange-100 text-orange-700 text-xs rounded font-medium">{promoCode}</span>}
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="mt-3">
                      <Label htmlFor="promo-code">Promo Code <span className="font-normal text-gray-400">(for ROI tracking)</span></Label>
                      <Input
                        id="promo-code"
                        value={promoCode}
                        onChange={(e) => setPromoCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                        placeholder="e.g. SAVE20"
                        maxLength={20}
                        className={`font-mono mt-1 ${errors.promoCode ? 'border-red-500' : ''}`}
                        data-testid="campaign-promo-code-input"
                      />
                      {errors.promoCode && <p className="text-xs text-red-500 mt-1">{errors.promoCode}</p>}
                      <p className="text-xs text-gray-500 mt-1">
                        Include this code in your message. We track when customers use it for ROI measurement.
                      </p>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>

            {/* Send Mode Gate */}
            {!sendModeUnlocked ? (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8">
                <div className="flex flex-col items-center gap-4 text-center py-4">
                  <div className="w-14 h-14 rounded-full bg-orange-50 border-2 border-orange-200 flex items-center justify-center">
                    <Lock className="w-7 h-7 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Ready to send to real customers?</h3>
                    <p className="text-sm text-gray-500 mt-1 max-w-sm mx-auto">Write your message and test it first. When you're ready, unlock to choose recipients and send.</p>
                  </div>
                  <Button
                    onClick={() => setSendModeUnlocked(true)}
                    className="mt-1"
                    data-testid="unlock-send-btn"
                  >
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Unlock Send Options
                  </Button>
                </div>
              </div>
            ) : (
            <>
            {/* Section 2: Customer Selection */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Users className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">Recipients</h2>
                    <p className="text-xs text-gray-500">Choose who will receive this campaign</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                {/* Target Type Selection */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button
                    type="button"
                    onClick={() => setTargetType('all')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      targetType === 'all' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="target-all-btn"
                  >
                    <Users className="w-5 h-5 text-gray-600 mb-2" />
                    <p className="font-medium text-gray-900">All Customers</p>
                    <p className="text-xs text-gray-500 mt-1">{customerCount.toLocaleString()} recipients</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setTargetType('segment')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      targetType === 'segment' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="target-segment-btn"
                  >
                    <Filter className="w-5 h-5 text-gray-600 mb-2" />
                    <p className="font-medium text-gray-900">Segment</p>
                    <p className="text-xs text-gray-500 mt-1">{segments.length} saved segments</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setTargetType('manual')}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      targetType === 'manual' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="target-manual-btn"
                  >
                    <Upload className="w-5 h-5 text-gray-600 mb-2" />
                    <p className="font-medium text-gray-900">Manual Entry</p>
                    <p className="text-xs text-gray-500 mt-1">{isEmailChannel ? 'Paste email addresses' : 'Paste or upload numbers'}</p>
                  </button>
                </div>
                
                {/* Segment Selection */}
                {targetType === 'segment' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <Label>Select Segment</Label>
                    <select
                      value={selectedSegmentId}
                      onChange={(e) => setSelectedSegmentId(e.target.value)}
                      className="w-full mt-1 px-3 py-2 border border-gray-200 rounded-lg"
                      data-testid="segment-select"
                    >
                      <option value="">Choose a segment...</option>
                      {segments.map(s => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Manual Entry */}
                {targetType === 'manual' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                    <Label>{isEmailChannel ? 'Email Addresses' : 'Phone Numbers'}</Label>
                    <Textarea
                      value={manualNumbers}
                      onChange={(e) => setManualNumbers(e.target.value)}
                      placeholder={isEmailChannel ? "john@example.com\njane@example.com\n..." : "+61412345678\n+61498765432\n..."}
                      rows={4}
                      className="mt-1 font-mono text-sm"
                      data-testid="manual-numbers-input"
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {isEmailChannel
                        ? 'Enter email addresses separated by new lines or commas'
                        : 'Enter phone numbers separated by new lines or commas. Use E.164 format (+614...)'}
                    </p>
                  </div>
                )}
                
                {errors.target && <p className="text-xs text-red-500">{errors.target}</p>}
                {errors.recipients && <p className="text-xs text-red-500">{errors.recipients}</p>}
              </div>
            </div>
            
            {/* Section 3: Time of Send */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-purple-600" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-gray-900">Send Time</h2>
                    <p className="text-xs text-gray-500">When should this campaign be sent?</p>
                  </div>
                </div>
              </div>
              <div className="p-4 sm:p-6 space-y-4">
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setSendType('now')}
                    className={`flex-1 p-4 rounded-lg border-2 text-left transition-all ${
                      sendType === 'now' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="send-now-btn"
                  >
                    <Zap className="w-5 h-5 text-gray-600 mb-2" />
                    <p className="font-medium text-gray-900">Send Now</p>
                    <p className="text-xs text-gray-500 mt-1">Campaign will be sent immediately</p>
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => setSendType('scheduled')}
                    className={`flex-1 p-4 rounded-lg border-2 text-left transition-all ${
                      sendType === 'scheduled' 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="send-scheduled-btn"
                  >
                    <Calendar className="w-5 h-5 text-gray-600 mb-2" />
                    <p className="font-medium text-gray-900">Schedule</p>
                    <p className="text-xs text-gray-500 mt-1">Choose date and time</p>
                  </button>
                </div>
                
                {sendType === 'scheduled' && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-lg space-y-3">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Date</Label>
                        <Input
                          type="date"
                          value={scheduledDate}
                          onChange={(e) => setScheduledDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="mt-1"
                          data-testid="schedule-date-input"
                        />
                      </div>
                      <div>
                        <Label>Time</Label>
                        <Input
                          type="time"
                          value={scheduledTime}
                          onChange={(e) => setScheduledTime(e.target.value)}
                          className="mt-1"
                          data-testid="schedule-time-input"
                        />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">
                      Timezone: {settings?.timezone || 'Australia/Sydney'}
                    </p>
                  </div>
                )}
                
                {/* Quiet Hours Warning */}
                <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Quiet Hours</p>
                      <p className="text-xs text-yellow-700 mt-0.5">
                        Messages cannot be sent between {quietHoursStart} - {quietHoursEnd}
                      </p>
                    </div>
                  </div>
                </div>
                
                {sendNowInQuietHours && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      <p className="text-sm text-red-700">
                        Currently within quiet hours ({quietHoursStart} - {quietHoursEnd}). &quot;Send Now&quot; is disabled.
                      </p>
                    </div>
                  </div>
                )}
                
                {scheduledInQuietHours && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      <p className="text-sm text-red-700">
                        Selected time falls within quiet hours. Please choose a different time.
                      </p>
                    </div>
                  </div>
                )}
                
                {errors.schedule && <p className="text-xs text-red-500">{errors.schedule}</p>}
              </div>
            </div>
            
            {/* Submit Errors */}
            {errors.submit && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-sm text-red-700">{errors.submit}</p>
                </div>
              </div>
            )}
            
            {/* Bottom Action Buttons */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={() => handleSubmit(true)}
                  disabled={submitting || !campaignName.trim()}
                  className="flex-1 justify-center"
                  data-testid="save-draft-bottom-btn"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Save Draft
                </Button>
                <Button
                  onClick={() => handleSubmit(false)}
                  disabled={submitting || !canSend || scheduledInQuietHours}
                  className="flex-1 justify-center"
                  data-testid="submit-campaign-bottom-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {submitting ? 'Processing...' : sendType === 'now' ? 'Send Campaign' : 'Schedule Campaign'}
                </Button>
              </div>
            </div>
            </>
            )}
          </div>
          
          {/* Sticky Sidebar — Phone Preview + Cost Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">

              {/* Live Preview */}
              {isEmailChannel ? (
                /* Email Preview */
                <div className="bg-gray-200 rounded-xl p-3 shadow-xl">
                  <div className="bg-white rounded-lg overflow-hidden border border-gray-300">
                    <div className="bg-gray-50 px-4 py-2 text-center text-xs text-gray-500 font-medium">
                      Email Preview
                    </div>
                    <div className="px-4 py-3 border-b border-gray-100 space-y-1">
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">From:</span> {emailFromName || 'Your Business'}
                      </div>
                      <div className="text-sm font-medium text-gray-900">
                        {emailSubject ? emailSubject.replace(/\{first_name\}/g, 'Sarah') : 'Subject line...'}
                      </div>
                    </div>
                    <div className="p-4 min-h-[180px] max-h-[400px] overflow-y-auto text-sm text-gray-800">
                      {emailHtml.trim() ? (
                        <div dangerouslySetInnerHTML={{
                          __html: emailHtml
                            .replace(/\{first_name\}/g, 'Sarah')
                            .replace(/\{business_name\}/g, settings?.name || 'Your Business')
                        }} />
                      ) : (
                        <div className="flex items-center justify-center h-20 text-sm text-gray-300">
                          Start writing your email...
                        </div>
                      )}
                    </div>
                    <div className="px-4 py-2 border-t border-gray-100 text-center">
                      <span className="text-xs text-gray-400 underline">Unsubscribe</span>
                    </div>
                  </div>
                </div>
              ) : (
                /* SMS Phone Preview */
                <div className="bg-gray-800 rounded-[2rem] p-2 shadow-xl">
                  <div className="bg-white rounded-[1.5rem] overflow-hidden">
                    <div className="bg-gray-100 px-4 py-2 text-center text-xs text-gray-500 font-medium">
                      SMS Preview
                    </div>
                    <div className="p-4 min-h-[180px] max-h-[320px] overflow-y-auto">
                      {messageBody.trim() ? (
                        <>
                          <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap break-words">
                            {messageBody.replace(/\{first_name\}/g, 'Sarah')}
                            {!messageBody.toLowerCase().includes('opt out: reply stop') && (
                              <span className="text-gray-400">{SMS_OPT_OUT_SUFFIX}</span>
                            )}
                          </div>
                          <div className="text-xs text-gray-400 mt-1.5 flex justify-between">
                            <span>{messageInfo.totalChars} chars</span>
                            <span className={messageInfo.segments > 1 ? 'text-orange-500 font-medium' : ''}>
                              {messageInfo.segments} SMS
                            </span>
                          </div>
                        </>
                      ) : (
                        <div className="flex items-center justify-center h-24 text-sm text-gray-300">
                          Start typing your message...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Cost / Balance Summary */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <h3 className="font-semibold text-gray-900 text-sm">
                    {sendModeUnlocked ? 'Campaign Summary' : 'Your Account'}
                  </h3>
                </div>
                <div className="p-4 space-y-3">

                  {sendModeUnlocked && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Recipients</span>
                        <span className="font-medium text-gray-900">{recipientCount.toLocaleString()}</span>
                      </div>
                      {!isEmailChannel && (
                        <>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">SMS per recipient</span>
                            <span className="font-medium text-gray-900">
                              {costEstimate?.has_personalisation && costEstimate.min_segments_per_msg !== costEstimate.max_segments_per_msg
                                ? `${costEstimate.min_segments_per_msg}–${costEstimate.max_segments_per_msg}`
                                : costEstimate?.has_personalisation
                                  ? costEstimate.avg_segments_per_msg
                                  : messageInfo.segments}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-gray-600">Total SMS</span>
                            <span className="font-medium text-gray-900">
                              {costEstimate ? costEstimate.total_segments.toLocaleString() : totalSmsRequired.toLocaleString()}
                            </span>
                          </div>
                          {costEstimate?.has_personalisation && (
                            <p className="text-xs text-orange-600">
                              avg {costEstimate.avg_segments_per_msg} SMS/recipient — varies by name length
                            </p>
                          )}
                        </>
                      )}
                      {isEmailChannel && (
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Cost</span>
                          <span className="font-medium text-green-600">Free</span>
                        </div>
                      )}
                      <div className="border-t border-gray-100" />
                    </>
                  )}

                  {!isEmailChannel && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600 flex items-center gap-1">
                          <Wallet className="w-4 h-4" />
                          SMS credits
                        </span>
                        <span className="font-medium text-gray-900">{smsBalance.toLocaleString()}</span>
                      </div>
                      {sendModeUnlocked && (
                        <div className="flex justify-between items-center mt-1">
                          <span className="text-xs text-gray-500">After send</span>
                          <span className={`text-xs font-medium ${smsAfterSend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {Math.max(0, smsAfterSend).toLocaleString()}
                          </span>
                        </div>
                      )}

                      <div className="border-t border-gray-100 pt-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-600">Wallet balance</span>
                          <span className="font-medium text-gray-900">${((walletStatus?.wallet_balance_cents || 0) / 100).toFixed(2)}</span>
                        </div>
                      </div>
                    </>
                  )}

                  {sendModeUnlocked && (
                    <>
                      <div className={`p-3 rounded-lg ${canSend ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                        <div className="flex items-center gap-2">
                          {canSend ? (
                            <>
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <span className="text-sm font-medium text-green-700">Ready to send</span>
                            </>
                          ) : isEmailChannel ? (
                            <>
                              <AlertCircle className="w-5 h-5 text-gray-400" />
                              <span className="text-sm font-medium text-gray-600">
                                {!selectedSenderId ? 'Verify a sender email first' : 'Complete email details'}
                              </span>
                            </>
                          ) : totalSmsRequired === 0 ? (
                            <>
                              <AlertCircle className="w-5 h-5 text-gray-400" />
                              <span className="text-sm font-medium text-gray-600">Add recipients and message</span>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="w-5 h-5 text-red-500" />
                              <span className="text-sm font-medium text-red-700">Not enough SMS credits</span>
                            </>
                          )}
                        </div>
                      </div>

                      {!isEmailChannel && !canAfford && totalSmsRequired > 0 && (
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={() => navigate('/dashboard/wallet')}
                        >
                          <Wallet className="w-4 h-4 mr-2" />
                          Buy SMS credits
                        </Button>
                      )}

                      {errors.balance && (
                        <p className="text-xs text-red-500">{errors.balance}</p>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Send Type Summary — only when unlocked */}
              {sendModeUnlocked && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                  <div className="flex items-center gap-2 text-sm">
                    {sendType === 'now' ? (
                      <>
                        <Zap className="w-4 h-4 text-orange-500" />
                        <span className="text-gray-700">Will send <strong>immediately</strong></span>
                      </>
                    ) : (
                      <>
                        <Calendar className="w-4 h-4 text-purple-500" />
                        <span className="text-gray-700">
                          Scheduled for{' '}
                          <strong>
                            {scheduledDate && scheduledTime
                              ? `${scheduledDate} at ${scheduledTime}`
                              : 'Not set'}
                          </strong>
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tag info dialog — shown once on first tag insert */}
      <AlertDialog open={showTagInfoDialog} onOpenChange={setShowTagInfoDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Personalisation tags & SMS costs</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-gray-600">
                <p>
                  Tags like <code className="bg-gray-100 px-1 rounded">{'{first_name}'}</code> get replaced with each customer's real name before sending.
                </p>
                <p>
                  Because names vary in length, the actual SMS count per recipient may differ from the estimate shown. For example, a message that's 1 SMS for "Jo" might be 2 SMS for "Alexander".
                </p>
                <p className="font-medium text-gray-700">
                  The accurate cost will be calculated based on your real customer data before you confirm sending.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Send/Schedule confirmation modal */}
      <AlertDialog open={showSendConfirm} onOpenChange={setShowSendConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {sendType === 'now' ? 'Send Campaign Now?' : 'Schedule Campaign?'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                <div className="space-y-2 text-gray-700">
                  <div className="flex justify-between">
                    <span>Campaign</span>
                    <span className="font-medium">{campaignName}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Recipients</span>
                    <span className="font-medium">{sendConfirmData?.backendRecipientCount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Target</span>
                    <span className="font-medium">{getTargetDescription()}</span>
                  </div>
                  {isEmailChannel ? (
                    <div className="flex justify-between">
                      <span>Cost</span>
                      <span className="font-bold text-green-600">Free</span>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between">
                        <span>Total SMS</span>
                        <span className="font-medium">{sendConfirmData?.effectiveSms?.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Estimated cost</span>
                        <span className="font-bold">${((sendConfirmData?.effectiveCostCents || 0) / 100).toFixed(2)}</span>
                      </div>
                    </>
                  )}
                  {sendType === 'scheduled' && scheduledDate && scheduledTime && (
                    <div className="flex justify-between">
                      <span>Scheduled</span>
                      <span className="font-medium">{scheduledDate} at {scheduledTime}</span>
                    </div>
                  )}
                </div>

                {sendConfirmData?.backendRecipientCount > 100 && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="text-red-700 font-medium">
                      This will send to {sendConfirmData.backendRecipientCount.toLocaleString()} people!
                    </p>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                // Save send data and request OTP
                setPendingSendData({
                  targetFilter: sendConfirmData?.targetFilter,
                  backendRecipientCount: sendConfirmData?.backendRecipientCount,
                });
                setShowSendConfirm(false);
                setOtpSending(true);
                setOtpError(null);
                setOtpCode('');

                try {
                  const token = await getToken();

                  // Need campaign ID to request OTP — create as draft first if needed
                  let campaignId = pendingSendData?.campaignId;
                  if (!campaignId) {
                    const selectedSender = senderIdentities.find(i => i.id === selectedSenderId);
                    const payload = {
                      name: campaignName,
                      channel,
                      message_template: isEmailChannel ? (emailHtml || 'Email campaign') : messageBody,
                      promo_code: promoCode.trim() || null,
                      target_filter: sendConfirmData?.targetFilter,
                      // Email-specific fields
                      ...(isEmailChannel && {
                        email_subject: emailSubject,
                        email_html: emailHtml,
                        email_from_name: emailFromName || undefined,
                        email_reply_to: selectedSender?.identity_value || undefined,
                      }),
                    };
                    const createRes = await fetch(`${BACKEND_URL}/api/campaigns`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                      body: JSON.stringify(payload),
                    });
                    if (!createRes.ok) {
                      const err = await createRes.json().catch(() => ({}));
                      throw new Error(err.detail || 'Failed to create campaign');
                    }
                    const campaign = await createRes.json();
                    campaignId = campaign.id;
                  }

                  setPendingSendData(prev => ({ ...prev, campaignId }));

                  const otpRes = await fetch(`${BACKEND_URL}/api/campaigns/${campaignId}/request-otp`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                  });
                  const otpData = await otpRes.json();

                  if (!otpRes.ok) {
                    if (otpData.detail?.error_code === 'NO_MOBILE_SET') {
                      setOtpError('Add your mobile number in Settings to send campaigns');
                      setShowOtpDialog(true);
                    } else {
                      throw new Error(otpData.detail?.message || otpData.detail || 'Failed to request OTP');
                    }
                  } else {
                    setMaskedPhone(otpData.masked_phone);
                    setShowOtpDialog(true);
                  }
                } catch (err) {
                  setOtpError(err.message);
                  setShowOtpDialog(true);
                } finally {
                  setOtpSending(false);
                }
              }}
              className="bg-orange-500 hover:bg-orange-600"
              disabled={otpSending}
            >
              {otpSending ? (
                <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Sending code...</>
              ) : (
                sendType === 'now' ? 'Send Now' : 'Schedule'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* OTP Verification Dialog */}
      <AlertDialog open={showOtpDialog} onOpenChange={(open) => {
        if (!open) {
          setShowOtpDialog(false);
          setOtpCode('');
          setOtpError(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-orange-500" />
              Verify to {sendType === 'now' ? 'send' : 'schedule'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                {otpError && !maskedPhone ? (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                    {otpError}
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-600">
                      A 6-digit code was sent to <span className="font-medium">{maskedPhone}</span>
                    </p>
                    <div>
                      <Input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        placeholder="000000"
                        value={otpCode}
                        onChange={(e) => {
                          setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6));
                          setOtpError(null);
                        }}
                        className="text-center text-2xl tracking-[0.5em] font-mono"
                        autoFocus
                      />
                    </div>
                    {otpError && (
                      <p className="text-sm text-red-600">{otpError}</p>
                    )}
                  </>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => {
              setShowOtpDialog(false);
              setOtpCode('');
              setOtpError(null);
            }}>Cancel</AlertDialogCancel>
            {maskedPhone && (
              <>
                <Button
                  variant="outline"
                  disabled={otpSending}
                  onClick={async () => {
                    setOtpSending(true);
                    setOtpError(null);
                    try {
                      const token = await getToken();
                      const res = await fetch(`${BACKEND_URL}/api/campaigns/${pendingSendData?.campaignId}/request-otp`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                      });
                      if (!res.ok) {
                        const data = await res.json().catch(() => ({}));
                        throw new Error(data.detail?.message || 'Failed to resend code');
                      }
                      setOtpCode('');
                      setOtpError(null);
                    } catch (err) {
                      setOtpError(err.message);
                    } finally {
                      setOtpSending(false);
                    }
                  }}
                >
                  {otpSending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Resend Code
                </Button>
                <Button
                  disabled={otpCode.length !== 6 || otpVerifying}
                  onClick={async () => {
                    setOtpVerifying(true);
                    setOtpError(null);
                    try {
                      await executeSendWithOtp(
                        pendingSendData?.targetFilter,
                        pendingSendData?.backendRecipientCount,
                        pendingSendData?.campaignId,
                        otpCode
                      );
                    } catch (err) {
                      if (err.message?.includes('INVALID_OTP') || err.otpInvalid) {
                        setOtpError('Invalid or expired code. Try resending.');
                      } else {
                        setOtpError(err.message);
                      }
                    } finally {
                      setOtpVerifying(false);
                    }
                  }}
                  className="bg-orange-500 hover:bg-orange-600 text-white"
                >
                  {otpVerifying ? (
                    <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Verifying...</>
                  ) : (
                    `Verify & ${sendType === 'now' ? 'Send' : 'Schedule'}`
                  )}
                </Button>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
